/**
 * UranIA PA — SPA entrypoint (US-009).
 *
 * Catena di provider:
 *   1. QueryClientProvider (TanStack Query) — server state.
 *   2. I18nextProvider (react-i18next) — i18n it-IT primario + en-EU fallback.
 *   3. RouterProvider (TanStack Router) — route tree (US-009 placeholder).
 *
 * Lo `UserManager` OIDC (oidc-client-ts) NON viene istanziato a startup:
 * sarà inizializzato lazy da US-010 (login page) — evita round-trip
 * Keycloak forzato per pagine pubbliche del scheleton.
 */

import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from '@tanstack/react-router';

import './styles/index.css';
import { initI18n } from './lib/i18n/index.ts';
import { loadSpaEnv } from './lib/env.ts';
import { createUraniaQueryClient } from './lib/query-client.ts';
import { router } from './router.tsx';

const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error('Root element #root not found in index.html');
}

// Fail-fast di configurazione (vedi lib/env.ts): una VITE_* richiesta
// mancante/vuota in build di produzione deve fermare l'avvio qui, prima
// del primo render — non arrivare come richiesta silenziosa verso
// `localhost` nel browser dell'utente. Messaggio in pagina (non solo
// console) perché chi verifica un deploy non apre sempre gli strumenti
// di sviluppo.
try {
  loadSpaEnv();
} catch (error) {
  const message = error instanceof Error ? error.message : String(error);
  const pre = document.createElement('pre');
  pre.style.cssText =
    'margin:0;padding:2rem;font-family:monospace;white-space:pre-wrap;color:#842029;background:#f8d7da;';
  pre.textContent = message;
  rootElement.replaceChildren(pre);
  throw error;
}

const queryClient = createUraniaQueryClient();

// Top-level await: target ES2022 (vite.config.ts), Vite gestisce il code-split
// asincrono dell'entry chunk. Atteso: i18n risolto prima del primo render
// così `useTranslation()` ritorna i bundle in-memory invece dei key letterali.
await initI18n();

// E2E test bridge (US-030): solo nei build `--mode e2e` (vedi
// `package.json` -> `build:e2e`). Vite resolve `import.meta.env.MODE` a
// build time: nei bundle production la branch e' un letterale `false` e
// l'`await import(...)` viene tree-shaked -- niente codice di test
// imbarcato negli artefatti production (verificato con grep su dist/).
if (import.meta.env.MODE === 'e2e') {
  const { installE2eBridge } = await import('./lib/auth/e2e-bridge.ts');
  installE2eBridge();
}

createRoot(rootElement).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  </StrictMode>,
);
